import { Link } from 'react-router'

import type { NavItem } from '@/app/navigation'
import { cn } from 'cn'

import { NavBadge } from './nav-badge'
import { sidebarTileClassName } from './sidebar-tile'
import { useNavItemActive } from './use-nav-item-active'
import { useNavItems } from './use-nav-items'

export function SidebarNav() {
  const items = useNavItems()
  return (
    <nav aria-label="Main" className="flex flex-col items-center">
      {items.map((item) => (
        <SidebarLink key={item.to} item={item} />
      ))}
    </nav>
  )
}

/** Rail item shape: small icon above its label, with a thin separator line underneath. */
const itemClassName = cn(sidebarTileClassName, 'h-auto w-16 flex-col gap-1 rounded-none border-b border-sidebar-foreground/15 px-1 py-3 hover:bg-transparent')
const labelClassName = 'max-w-full truncate text-[10px] leading-tight font-normal'

/**
 * Icon-above-label link. Active: brand-coloured icon and label.
 * Uses `Link` + `useNavItemActive` rather than `NavLink`: the tooltip's Slot can't merge NavLink's function-style props.
 */
function SidebarLink({ item }: { item: NavItem }) {
  const { icon: Icon } = item
  const isActive = useNavItemActive(item)

  if (item.comingSoon) {
    // Not a link: nothing to open yet.
    return (
      <button type="button" aria-disabled="true" aria-label={`${item.label}, coming soon`} title={`${item.label} · Coming soon`} className={cn(itemClassName, 'cursor-not-allowed opacity-40 hover:text-sidebar-foreground/55')}>
        <Icon className="size-5" />
        <span className={labelClassName}>{item.label}</span>
      </button>
    )
  }

  return (
    <Link to={item.to} aria-current={isActive ? 'page' : undefined} className={cn(itemClassName, isActive && 'text-primary hover:text-primary')}>
      <Icon className="size-5" />
      <span className={labelClassName}>{item.label}</span>
      {item.unreadBadge && <NavBadge className="top-2 right-3" />}
    </Link>
  )
}
